<template>
    <dialog-modal
        v-if="shouldRender && hasAnyPermission('asset.update_operability')"
        :is-processing="form.processing"
        :primary-button-label="trans('general.action.confirm')"
        :show="show"
        :submit-disabled="!canSubmit"
        @close="closeModal"
        @submit="handleSubmit"
    >
        <template #title>
            <asset-short-info v-if="asset" :asset="asset" class="my-4">
                <template #top>
                    <status-badge :enable-mobile="true" :operability="asset.status?.operability" />
                </template>
            </asset-short-info>
        </template>

        <div
            v-html="
                trans('assets.modal.operability_change.description', {
                    operability: operabilityLabel,
                })
            "
        ></div>

        <form @submit.prevent="handleSubmit">
            <row v-if="attestationRequired" class="mt-3">
                <column>
                    <jet-checkbox
                        v-model="form.tested_and_calibrated"
                        :invalid-feedback="form.errors.tested_and_calibrated"
                        :label="trans('assets.modal.operability_change.attestation.label')"
                    />
                </column>
            </row>

            <row v-if="reasons && reasons.length && form.operability === 'inoperable'" class="mt-3">
                <column>
                    <b>{{ trans('assets.modal.operability_change.reason.label') }}</b>
                    <input-radio-group v-model="form.reason" :invalid-feedback="form.errors.reason" :options="reasons" />
                </column>
            </row>

            <row class="mt-3">
                <column>
                    <jet-textarea
                        v-model="form.additionalInformation"
                        :horizontal="false"
                        :invalidFeedback="form.errors.additionalInformation"
                        :label="trans('assets.modal.operability_change.additional_information.label')"
                        :maxlength="80"
                        :placeholder="trans('assets.modal.operability_change.additional_information.placeholder')"
                        :rows="4"
                        name="additional_information"
                    />
                </column>
            </row>
        </form>
    </dialog-modal>
</template>
<script setup>
import DialogModal from '@/Jetstream/DialogModal.vue';
import JetCheckbox from '@/Jetstream/Checkbox.vue';
import JetTextarea from '@/Jetstream/Textarea.vue';
import { eventBus, events } from '@/eventBus';

import InputRadioGroup from '@/Components/InputRadioGroup.vue';
import AssetShortInfo from '@/Components/AssetShortInfo.vue';
import StatusBadge from '@/Components/StatusBadge.vue';
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue';
import { useForm, usePage } from '@inertiajs/vue3';
import { trans } from '@/mixins/i18n.js';

defineOptions({
    name: 'ConfirmAssetStatusChangeModal',
});

defineProps({
    asset: { type: Object, default: null },
});

const emit = defineEmits(['cancel']);
const page = usePage();

const shouldRender = ref(false);
const show = ref(false);
const assetId = ref(null);

const form = useForm({
    operability: null,
    additionalInformation: '',
    reason: 'corrective_maintenance_required',
    tested_and_calibrated: false,
});

const reasons = computed(() => page.props.assetOperabilityChangeReasons?.data ?? []);

const attestationRequired = computed(() => page.props.assetReintegrationAttestationRequired === true && form.operability === 'operable');

const canSubmit = computed(() => !attestationRequired.value || form.tested_and_calibrated === true);

const operabilityLabel = computed(() => {
    const text = trans(`assets.modal.operability_change.label.${form.operability}`);
    const cssClass = form.operability === 'inoperable' ? 'bg-danger' : form.operability === 'operable' ? 'bg-success' : 'bg-secondary';

    return `<span><em
            class="operability-indicator ${cssClass} ${form.operability}"></em>&nbsp;<b>${text}</b></span>`;
});

function closeModal() {
    show.value = false;
    setTimeout(() => {
        shouldRender.value = false;
    }, 300);

    eventBus.$emit(events.cancelConfirmAssetStatusChangeModal);
    emit('cancel');
}

function transformData(data) {
    if (data.operability === 'operable') {
        const payload = {
            operability: 'operable',
            additionalInformation: data.additionalInformation || null,
        };
        if (attestationRequired.value) {
            payload.tested_and_calibrated = data.tested_and_calibrated;
        }

        return payload;
    }

    return data;
}

function handleSubmit() {
    if (!canSubmit.value) {
        return;
    }
    form.transform(transformData).post(route('assets.update-operability', assetId.value), {
        preserveState: true,
        onSuccess: () => {
            eventBus.$emit(events.updateNotifications);

            eventBus.$emit(events.assetOperabilityUpdated, {
                asset: assetId.value,
                ...form.data(),
            });

            closeModal();
        },
    });
}

const onOpen = ({ assetId: newAssetId, operability }) => {
    assetId.value = newAssetId;

    form.operability = operability;
    form.additionalInformation = '';
    form.reason = 'corrective_maintenance_required';
    form.tested_and_calibrated = false;
    form.clearErrors();

    shouldRender.value = true;
    nextTick(() => (show.value = true));
};

onMounted(() => {
    eventBus.$on(events.openConfirmAssetStatusChangeModal, onOpen);
});

onBeforeUnmount(() => {
    eventBus.$off(events.openConfirmAssetStatusChangeModal, onOpen);
});
</script>
<style scoped>
:deep(textarea) {
    resize: none;
}
</style>
